/**
 * nfcIntegrity — tell a COMPLETE wristband read apart from a BAD one.
 *
 * A read that matched no band is not the same failure as a read that never
 * completed, and the operator must be told which:
 *
 *   • read OK but unregistered — every byte came off the chip; the identifier
 *     is well-formed; it simply matches nobody. NOT a bad read.
 *   • bad read — nothing came back, or the value is truncated / garbled (card
 *     pulled away, reader glitch, half-decoded credential). Retry.
 *
 * Integrity is decided from the identifier's SHAPE, which truncation breaks:
 *   - a Shaka credential is a UUID → 32 hex once separators are stripped.
 *   - a raw chip UID is a 4 / 7 / 10-byte anticollision id → 8 / 14 / 20 hex;
 *     it is read atomically, so a wrong length here is garbage, not a short card.
 *
 * On the deck the Shaka Bridge hands us the credential and the chip UID as
 * SEPARATE fields, so — unlike the phone — we know the provenance directly and
 * never have to infer it. Mirrors the shaka-api `is_complete_nfc_identifier`
 * check so both sides draw the line in the same place.
 */

/** Canonical form — uppercase hex, separators stripped (matches the backend's
 *  `normalize_nfc_uid`, the value the server actually hashes). */
function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/[:\-\s]/g, '').toUpperCase();
}

const CREDENTIAL_HEX_LEN = 32;
const CHIP_UID_HEX_LENS = new Set([8, 14, 20]);

function isHex(value: string): boolean {
  return /^[0-9A-F]+$/.test(value);
}

export function isCredentialShape(value: string | null | undefined): boolean {
  const v = normalize(value);
  return isHex(v) && v.length === CREDENTIAL_HEX_LEN;
}

export function isChipUidShape(value: string | null | undefined): boolean {
  const v = normalize(value);
  return isHex(v) && CHIP_UID_HEX_LENS.has(v.length);
}

/** The raw scan payload the bridge delivers: a written credential (paired band)
 *  and/or a raw chip UID (any card). */
export interface NfcScanPayload {
  credential?: string;
  uid?: string;
}

/** 'ok' carries the identifier to send onward; 'bad_read' means nothing usable
 *  was read in full — retry, and never present it as "not registered". */
export type NfcScanOutcome =
  | { status: 'ok'; identifier: string }
  | { status: 'bad_read' };

/**
 * Classify a bridge scan into a complete identifier or a bad read, honouring
 * the same ``prefer`` contract as the lookup: the written credential by default
 * (chip UID as the fallback for a never-paired card), or the raw UID outright.
 * The returned ``identifier`` is the RAW value to send — the server normalises.
 */
export function classifyScan(
  payload: NfcScanPayload,
  prefer: 'credential' | 'uid' = 'credential',
): NfcScanOutcome {
  const credential = payload.credential ?? '';
  const uid = payload.uid ?? '';

  if (prefer === 'uid') {
    return isChipUidShape(uid) ? { status: 'ok', identifier: uid } : { status: 'bad_read' };
  }

  // Credential preferred, chip UID as the never-paired fallback.
  if (credential) {
    return isCredentialShape(credential) ? { status: 'ok', identifier: credential } : { status: 'bad_read' };
  }
  if (uid) {
    return isChipUidShape(uid) ? { status: 'ok', identifier: uid } : { status: 'bad_read' };
  }
  return { status: 'bad_read' };
}
